import {InfoIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useEffect, useId, useRef, useState} from 'react'
import type {
  CopilotReviewerRow,
  CopilotReviewEffortOption,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {PreviewRequiredNote} from '@github-ui/copilot-code-review-controls/PreviewFeaturesNotice'
import {ActionList, ActionMenu, Label, Link} from '@primer/react'
import {
  copilotReviewEffortAvailabilityBadge,
  copilotReviewEffortLabel,
  normalizeCopilotReviewEffort,
  isCopilotReviewEffortAvailable,
  isCopilotReviewEffortPreviewRequired,
  type CopilotReviewEffortValue,
} from './copilot-review-effort'
import styles from './Reviewers.module.css'

type Props = {
  row: CopilotReviewerRow
  selectedEffort: CopilotReviewEffortValue | undefined
  onSelectEffort: (effort: CopilotReviewEffortValue) => void
  disabled?: boolean
  onCloseWithoutTarget?: () => void
}

function NotifyOnUnmountWhileOpen({
  openRef,
  onCloseWithoutTarget,
}: {
  openRef: {current: boolean}
  onCloseWithoutTarget?: () => void
}) {
  useEffect(() => {
    return () => {
      if (!openRef.current) return
      openRef.current = false
      onCloseWithoutTarget?.()
    }
  }, [openRef, onCloseWithoutTarget])

  return null
}

export function CopilotReviewEffortMenu({
  row,
  selectedEffort,
  onSelectEffort,
  disabled = false,
  onCloseWithoutTarget,
}: Props) {
  const descriptionId = useId()
  const openRef = useRef(false)
  const [open, setOpen] = useState(false)
  const label = copilotReviewEffortLabel(row, selectedEffort) ?? 'Choose'
  const optionCount = row.reviewEffortOptions.length
  const menuDisabled = disabled || !row.requestable || optionCount === 0
  const documentationUrl = row.learnMoreUrl?.trim()
  if (menuDisabled && open) setOpen(false)

  if (optionCount === 0) return null

  return (
    <>
      <NotifyOnUnmountWhileOpen openRef={openRef} onCloseWithoutTarget={onCloseWithoutTarget} />
      <ActionMenu
        open={open && !menuDisabled}
        onOpenChange={nextOpen => {
          const next = !menuDisabled && nextOpen
          openRef.current = next
          setOpen(next)
        }}
      >
        <span id={descriptionId} className="sr-only">
          Copilot review effort
        </span>
        <ActionMenu.Button
          variant="invisible"
          size="small"
          trailingAction={TriangleDownIcon}
          className={styles.effortMenuButton}
          aria-describedby={descriptionId}
          inactive={menuDisabled || undefined}
          aria-disabled={menuDisabled || undefined}
        >
          {label}
        </ActionMenu.Button>
        <ActionMenu.Overlay align="end" width="auto" variant={{narrow: 'fullscreen'}}>
          <ActionList>
            <ActionList.Group selectionVariant="single">
              {row.reviewEffortOptions.map(option => (
                <EffortOption
                  key={option.label}
                  option={option}
                  selectedCopilotEffort={selectedEffort}
                  menuDisabled={menuDisabled}
                  onSelectEffort={onSelectEffort}
                />
              ))}
            </ActionList.Group>
          </ActionList>
          <div className={styles.effortFooter}>
            <InfoIcon size={16} className="color-fg-muted" />
            <span className={styles.effortFooterText}>
              <span>
                <strong>Applies to this pull request for everyone.</strong>
              </span>
              {documentationUrl ? (
                <Link href={documentationUrl} inline>
                  Learn more about Copilot code review.
                </Link>
              ) : null}
            </span>
          </div>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}

function EffortOption({
  option,
  selectedCopilotEffort,
  menuDisabled,
  onSelectEffort,
}: {
  option: CopilotReviewEffortOption
  selectedCopilotEffort: string | undefined
  menuDisabled: boolean
  onSelectEffort: (effort: CopilotReviewEffortValue) => void
}) {
  const copilotEffortValue = normalizeCopilotReviewEffort(option.value)
  const selected = copilotEffortValue !== undefined && copilotEffortValue === selectedCopilotEffort
  const previewRequired = isCopilotReviewEffortPreviewRequired(option.availability)
  const disabled = menuDisabled || !isCopilotReviewEffortAvailable(option.availability) || !copilotEffortValue
  const badge = copilotReviewEffortAvailabilityBadge(option.availability)

  return (
    <ActionList.Item
      selected={selected}
      disabled={disabled}
      onSelect={() => {
        if (disabled || !copilotEffortValue || selected) return
        onSelectEffort(copilotEffortValue)
      }}
    >
      {option.label}
      {badge ? (
        <span className="ml-2">
          <Label size="small" variant={badge.variant}>
            {badge.label}
          </Label>
        </span>
      ) : null}
      <ActionList.Description variant="block">
        {option.description}
        {previewRequired ? (
          <span className="d-block mt-1">
            <PreviewRequiredNote />
          </span>
        ) : null}
      </ActionList.Description>
    </ActionList.Item>
  )
}
